/* Notification Center — Recipient Group → Members → Users tab.
 *
 * Adds INDIVIDUAL users to a Recipient Group, next to the Role / Department members
 * of the Roles tab. Every active user comes from the live Users data source
 * (GET recipient-users — same origin as Settings → Users); a group stores only the
 * user's existing id + its In App / Email / SMS ticks. Name, roles and department
 * are shown here but never saved with the group.
 *
 * It is the "Users" tab of the dialog's Add Recipients section; what is selected
 * shows in the shared Selected Members grid above (RecipientsEditor), where it can
 * also be removed. Same behaviour as the Roles tab: the row checkbox
 * adds / removes the user straight away, Notify Via edits a selected user's
 * channels (or stages them for an unselected one), and the search box filters by
 * the user's name, login, Role and Department.
 *
 * CONTROLLED: the parent owns `users` (the group's selected users) and receives
 * every change through onChange, so switching tabs never loses a selection. */
import { useEffect, useMemo, useState } from 'react';
import { getRecipientUsers, type RecipientUser } from './api';
import { groupUserName, type NcGroupUser } from './data';

type Chan = { inApp: boolean; email: boolean; sms: boolean };
/** A newly ticked user gets the same default channels as a newly ticked role. */
const DEFAULT_CHAN: Chan = { inApp: true, email: true, sms: false };
const CHANNELS = [['inApp', 'In App'], ['email', 'Email'], ['sms', 'SMS']] as const;


export function GroupUsersEditor({ users, onChange }: {
  /** The group's individually selected users. */
  users: NcGroupUser[];
  onChange: (next: NcGroupUser[]) => void;
}) {
  const [options, setOptions] = useState<RecipientUser[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState('');
  // Channels chosen on a row BEFORE it is ticked (applied when it is added).
  const [staged, setStaged] = useState<Record<string, Chan>>({});

  useEffect(() => {
    let alive = true;
    getRecipientUsers()
      .then((rows) => { if (alive) setOptions(rows); })
      .catch(() => { if (alive) { setOptions([]); setFailed(true); } });
    return () => { alive = false; };
  }, []);

  const selected = useMemo(() => new Map(users.map((u) => [u.userId, u])), [users]);

  // Selected users that are no longer in the active list (deleted / deactivated)
  // stay visible so they can be removed — they are never silently dropped.
  const rows = useMemo(() => {
    const live: (RecipientUser & { missing?: boolean })[] = options ?? [];
    const liveIds = new Set(live.map((o) => o.userId));
    const stale = users.filter((u) => !liveIds.has(u.userId)).map((u) => ({
      userId: u.userId, userName: u.userName, name: groupUserName(u), roles: u.roles ?? null, department: u.department ?? null, missing: true,
    }));
    return [...stale, ...live];
  }, [options, users]);

  const q = search.trim().toLowerCase();
  // Search covers the shown name AND the login, plus role and department.
  const visible = rows.filter((r) => !q
    || groupUserName(r).toLowerCase().includes(q)
    || (r.userName ?? '').toLowerCase().includes(q)
    || (r.roles ?? '').toLowerCase().includes(q)
    || (r.department ?? '').toLowerCase().includes(q));

  function toggleUser(r: RecipientUser) {
    if (selected.has(r.userId)) { onChange(users.filter((u) => u.userId !== r.userId)); return; }
    const c = staged[r.userId] ?? DEFAULT_CHAN;
    onChange([...users, {
      userId: r.userId, userName: r.userName, name: r.name ?? undefined, roles: r.roles ?? undefined, department: r.department ?? undefined,
      inApp: c.inApp, email: c.email, sms: c.sms, exists: true,
    }]);
  }
  function toggleChannel(r: RecipientUser, ch: keyof Chan) {
    const sel = selected.get(r.userId);
    if (sel) { onChange(users.map((u) => (u.userId === r.userId ? { ...u, [ch]: !u[ch] } : u))); return; }
    setStaged((cur) => { const c = cur[r.userId] ?? DEFAULT_CHAN; return { ...cur, [r.userId]: { ...c, [ch]: !c[ch] } }; });
  }

  return (
    <div className="nc-role-add nc-grp-users">
      <div className="nc-role-add-hd">
        <span>Available users</span>
        <input className="nc-finput nc-sm" placeholder="Search user / role / department…" aria-label="Search users"
          value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 260 }} />
      </div>
      <div className="nc-role-tblwrap">
        <table className="nc-tgt-table nc-role-tbl">
          <thead><tr>
            <th style={{ width: 40, textAlign: 'center' }}></th>
            <th>Users</th><th>Roles</th><th>Department</th><th style={{ width: 210 }}>Notify Via</th>
          </tr></thead>
          <tbody>
            {options === null && <tr><td colSpan={5} className="nc-role-empty">Loading users…</td></tr>}
            {options !== null && failed && <tr><td colSpan={5} className="nc-role-empty">Could not load users from the Users data source.</td></tr>}
            {options !== null && !failed && rows.length === 0 && <tr><td colSpan={5} className="nc-role-empty">No active users found.</td></tr>}
            {rows.length > 0 && visible.length === 0 && <tr><td colSpan={5} className="nc-role-empty">No users match “{search}”.</td></tr>}
            {visible.map((r) => {
              const sel = selected.get(r.userId);
              const chan: Chan = sel ? { inApp: sel.inApp, email: sel.email, sms: sel.sms } : (staged[r.userId] ?? DEFAULT_CHAN);
              return (
                <tr key={r.userId} onClick={() => toggleUser(r)} style={{ cursor: 'pointer' }}>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={!!sel} aria-label={`Select ${groupUserName(r)}`}
                      onClick={(e) => e.stopPropagation()} onChange={() => toggleUser(r)} />
                  </td>
                  <td title={r.userName && r.userName !== groupUserName(r) ? `Login: ${r.userName}` : undefined}>
                    {groupUserName(r)}
                    {r.missing && <span className="nc-role-nodept"> · no longer active</span>}
                  </td>
                  <td>{r.roles || <span className="nc-role-nodept">—</span>}</td>
                  <td>{r.department || <span className="nc-role-nodept">—</span>}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="nc-notify-via">
                      {CHANNELS.map(([ch, lbl]) => (
                        <label key={ch} className="nc-nv-check">
                          <input type="checkbox" checked={chan[ch]} onChange={() => toggleChannel(r, ch)} /> {lbl}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="nc-role-add-ft">
        <span className="nc-role-add-hint">
          {users.length ? `${users.length} user${users.length === 1 ? '' : 's'} selected` : 'Check a user to add them to the group; uncheck to remove.'}
        </span>
      </div>
    </div>
  );
}
